// Layer 4 (UI): Rule set view (#270, paul-fmea.A3). Renders `liveRuleSet()`
// against Paul's V8 Met Diagnostics workbook shape via RuleSetPanel.transform
// — all projection logic lives there, this file only renders the model.
// See docs/logic/advisory/rule-set.md.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Lock } from 'lucide-react';
import { Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { Case } from '@/versions/v1/schema/case';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { MetDiagnosticsLikelihood, MetDiagnosticsPriority } from '@/versions/v1/schema/met-diagnostics-cause';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import {
  liveRuleSet,
  pendingBandChangeForSection,
  ruleOutcome,
  ruleSetCounts,
  RULE_OUTCOME_LABEL,
} from '@/versions/v1/shared/RuleSetEngine';
import { liveControlPlanRows } from '@/versions/v1/shared/control-plan-band.derive';
import {
  labCheckLine,
  openCaseHrefForCauseId,
  PROVENANCE_LABEL,
  ruleSetPanelModel,
  sectionCoverageBar,
  whatToTrendLine,
  whatToTrendTooltip,
  workbookFigureLine,
  type RuleSetProvenanceKind,
  type RuleSetRuleEntry,
  type RuleSetSectionRow,
} from './RuleSetPanel.transform';
import { STAGE_INK_LINK, STAGE_ROWS } from './stage-body-ui';

/** A caps chip in the comp's pill recipe (10.5 px, 600, tracked). */
const RULE_CHIP = 'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

const PROVENANCE_TONE: Record<RuleSetProvenanceKind, string> = {
  workbook: 'text-[var(--ink-secondary)]',
  'case-learned': 'font-medium text-ink-primary',
  paper: 'text-[var(--ink-secondary)]',
};

function RuleEntryCard({ entry, muted }: { entry: RuleSetRuleEntry; muted?: boolean }) {
  return (
    <div
      className={`mt-2 border-l-2 pl-3 text-sm ${
        muted ? 'border-dashed border-[var(--hairline-strong)]' : 'border-divider'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted">{entry.rule.id}</span>
        <Pill tone="default">{entry.rule.priority}</Pill>
        {ruleOutcome(entry.rule) ? (
          <span className="text-xs text-muted">{RULE_OUTCOME_LABEL[ruleOutcome(entry.rule)!]}</span>
        ) : null}
        <span className={`text-xs ${PROVENANCE_TONE[entry.provenanceKind]}`}>
          <ProvenanceChip
            text={PROVENANCE_LABEL[entry.provenanceKind]}
            title={entry.provenanceText}
            compact
          />
        </span>
        {muted ? <span className="text-xs italic text-muted">proposed, not live</span> : null}
      </div>
      <dl className="mt-1.5 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
        <div>
          <dt className="text-muted">Diagnosis</dt>
          <dd className="font-medium text-ink-primary">{entry.rule.diagnosis}</dd>
        </div>
        <div>
          <dt className="text-muted">What to trend</dt>
          <dd className="text-ink-secondary" title={whatToTrendTooltip(entry.rule)}>
            {whatToTrendLine(entry.rule)}
          </dd>
        </div>
        <div>
          <dt className="text-muted">Escalation</dt>
          <dd className="text-ink-secondary" title={entry.ladder?.tooltip}>
            {entry.ladder?.chain ?? '—'}
          </dd>
        </div>
        {entry.rivalLabel ? (
          <div>
            <dt className="text-muted">Rules out</dt>
            <dd className="text-ink-secondary">{entry.rivalLabel}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-muted">Lab check</dt>
          <dd className="text-ink-secondary">{labCheckLine(entry.rule)}</dd>
        </div>
        <div>
          <dt className="text-muted">Immediate response</dt>
          <dd className="text-ink-secondary">{entry.rule.recommendedAction}</dd>
        </div>
      </dl>
    </div>
  );
}

/** Paul's cause-level Likelihood/Priority (#271) — rendered as a bare
 * `L# · P#` readout, never a `<Pill>`, so it cannot be mistaken for a rule's
 * `RulePriority` (`P1`–`P4`) badge, which sits inside `RuleEntryCard` below,
 * one level down, on a different quantity. */
function WorkbookFigure({
  row,
  className = '',
}: {
  row: { workbookLikelihood: MetDiagnosticsLikelihood; workbookPriority: MetDiagnosticsPriority };
  className?: string;
}) {
  return (
    <span
      className={`font-mono text-xs text-muted ${className}`}
      title="Workbook Likelihood / Priority (1–5, no worded anchor) — the cause's own figures, not the rule's severity"
    >
      {workbookFigureLine(row)}
    </span>
  );
}

/** Deep-link target: `?cause=MD-060` (`metDiagnosticsCauseHref`) routes here
 * the way a stage-ladder row's `nodeHref` routes to the plant model — the
 * `<li id>` this row shares with its section's `<details>` `open` state. */
function causeAnchorId(causeId: string): string {
  return `md-cause-${causeId}`;
}

function CauseRow({
  row,
  highlighted,
  openCaseHref,
}: {
  row: RuleSetSectionRow;
  highlighted: boolean;
  openCaseHref?: string;
}) {
  // A deep-linked cause takes the selection wash (DESIGN.md § Crimson: selection is `accent-selected`).
  const highlightClass = highlighted
    ? '-mx-2 rounded-lg bg-[color-mix(in_srgb,var(--color-accent-500)_8%,transparent)] px-2'
    : '';
  if (row.kind === 'gap') {
    return (
      <li
        id={causeAnchorId(row.causeId)}
        className={`py-2 text-sm text-muted ${highlightClass}`}
      >
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-xs">{row.causeId}</span>
          <span>{row.cause}</span>
          <span className="italic" title={row.gapReason}>gap</span>
          <WorkbookFigure row={row} className="ml-auto" />
        </div>
      </li>
    );
  }

  return (
    <li id={causeAnchorId(row.causeId)} className={`py-2 text-sm ${highlightClass}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-xs text-muted">{row.causeId}</span>
        <span className="font-medium text-ink-primary">{row.cause}</span>
        {row.affectsCode ? (
          <span className="font-mono text-xs text-muted">{row.affectsCode}</span>
        ) : null}
        {openCaseHref ? (
          <Link to={openCaseHref} className={STAGE_INK_LINK}>
            Open case →
          </Link>
        ) : null}
        <WorkbookFigure row={row} className="ml-auto" />
      </div>
      {row.kind === 'proposed' ? (
        <p className="mt-1 text-xs italic text-muted">proposed, not live</p>
      ) : null}
      {row.rules.map((entry) => (
        <RuleEntryCard key={entry.rule.id} entry={entry} muted={row.kind === 'proposed'} />
      ))}
    </li>
  );
}

function sectionSubtitle(section: {
  bandLine: string | null;
  bandZone: string | null;
  bandTooltip: string | null;
  target: string;
}): string | undefined {
  if (section.bandLine) {
    const zone = section.bandZone ? ` · ${section.bandZone}` : '';
    return `${section.bandLine}${zone}`;
  }
  if (section.target !== '—') {
    return `Target: ${section.target}`;
  }
  return undefined;
}

export function RuleSetPanel({
  advisory,
  cases,
  diagnosis,
  highlightCauseId,
}: {
  advisory: AdvisorySessionState;
  cases: readonly Case[];
  diagnosis?: Diagnosis;
  /** `?cause=MD-060` (`metDiagnosticsCauseHref`) — the section holding this
   * cause opens and the row scrolls into view once on arrival. */
  highlightCauseId?: string;
}) {
  const { projectId: slug = 'alpha' } = useParams<{ projectId: string }>();
  const { version, rules, proposed } = useMemo(() => liveRuleSet(advisory), [advisory]);
  const footerProposedCount = useMemo(() => ruleSetCounts(advisory).proposed, [advisory]);
  const bandChangeReview = useMemo(
    () => pendingBandChangeForSection('Zn Recovery Below Target', advisory),
    [advisory],
  );
  const planRows = useMemo(() => liveControlPlanRows(advisory.controlPlanSessionRows), [advisory]);
  const model = useMemo(
    () =>
      ruleSetPanelModel(version, rules, proposed, footerProposedCount, {
        cursor: DEFAULT_CURSOR,
        controlPlanRows: planRows,
        bandChangeReviewId: bandChangeReview?.id,
        projectSlug: slug,
      }),
    [version, rules, proposed, footerProposedCount, planRows, bandChangeReview?.id, slug],
  );

  // monotonic set, entries only ever added, never removed (review
  // SHOULD-FIX). The prior build derived `open` fresh from `highlightCauseId`
  // on every render (`open={sectionHasHighlight || undefined}`): flipping
  // that value back to `undefined` at any point would make React call
  // `removeAttribute('open')` and close the section out from under a reader
  // who left it open on purpose — React only re-commits a DOM attribute when
  // the prop value actually *changes*, so a set that never drops an entry
  // means `open` is `true` on every render for that section forever, React
  // never re-touches it after the first commit, and a user's own toggle is
  // never fought. The initializer covers the arriving deep link's first
  // paint (no flash of closed-then-open); the effect covers clicking a
  // second, different `?cause=` link without unmounting this panel.
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(() => {
    if (!highlightCauseId) return new Set();
    const target = model.sections.find((section) => section.rows.some((row) => row.causeId === highlightCauseId));
    return target ? new Set([target.section]) : new Set();
  });

  useEffect(() => {
    if (!highlightCauseId) return;
    const target = model.sections.find((section) => section.rows.some((row) => row.causeId === highlightCauseId));
    if (target) {
      setOpenSections((prev) => (prev.has(target.section) ? prev : new Set(prev).add(target.section)));
    }
    const el = document.getElementById(causeAnchorId(highlightCauseId));
    el?.scrollIntoView({ block: 'center' });
    // Deliberately keyed on `highlightCauseId` alone, not `model` — a
    // section already force-opened must not be touched again just because
    // the rule set recomputed for an unrelated reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightCauseId]);

  return (
    <div data-rule-set>
      <div className={STAGE_ROWS}>
        {model.sections.map((section) => {
          const subtitle = sectionSubtitle(section);
          const { coveredPct, proposedPct } = sectionCoverageBar(section);
          return (
            <details
              key={section.section}
              className="group"
              title={section.bandTooltip ?? undefined}
              open={openSections.has(section.section) || undefined}
            >
              <summary className="grid cursor-pointer list-none grid-cols-[minmax(0,1fr)_auto_auto_1rem] items-center gap-2.5 px-4 py-[7px] text-[13px] transition-colors duration-150 hover:bg-[var(--surface-1)] [&::-webkit-details-marker]:hidden">
                <span className="min-w-0 truncate">
                  <span className="font-semibold text-ink-primary">{section.section}</span>
                  {subtitle ? <span className="text-muted"> · {subtitle}</span> : null}
                </span>
                {section.bandChangeHref ? (
                  <Link to={section.bandChangeHref} className={cn(RULE_CHIP, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]')}>
                    Band change proposed
                  </Link>
                ) : <span />}
                <span className="flex items-center gap-2">
                  <span className="flex h-1.5 w-[5.6rem] overflow-hidden rounded-full bg-[var(--hairline)]" aria-hidden="true">
                    <i className="block bg-[var(--ink-secondary)]" style={{ width: `${coveredPct}%` }} />
                    <i className="block bg-[color-mix(in_srgb,var(--ink-primary)_18%,transparent)]" style={{ width: `${proposedPct}%` }} />
                  </span>
                  <span data-rule-section-coverage className={cn('min-w-[8.5rem] text-right text-xs tabular-nums', section.coveredCount > 0 ? 'text-muted' : 'text-[var(--state-warning)]')}>
                    {section.coveredCount} covered
                    {section.proposedCount > 0 ? ` · ${section.proposedCount} proposed` : ''} / {section.totalCauses}
                  </span>
                </span>
                <ChevronRight
                  className="h-3.5 w-3.5 shrink-0 text-muted transition-transform duration-150 group-open:rotate-90"
                  aria-hidden
                />
              </summary>
              <ul className={cn(STAGE_ROWS, 'border-t border-divider px-4 pb-1')}>
                {section.rows.map((row) => (
                  <CauseRow
                    key={row.causeId}
                    row={row}
                    highlighted={row.causeId === highlightCauseId}
                    openCaseHref={openCaseHrefForCauseId(slug, row.causeId, cases, diagnosis)}
                  />
                ))}
              </ul>
            </details>
          );
        })}

        {model.ungrouped.length > 0 || model.equipmentBand ? (
          <div className="px-4 py-[7px]" title={model.equipmentBand?.bandTooltip}>
            <div className="flex items-center gap-3">
              <p className="min-w-0 flex-1 text-[13px]">
                <span className="font-semibold text-ink-primary">Equipment / mechanical rules</span>
                <span className="text-muted">
                  {' · '}
                  {model.equipmentBand
                    ? model.equipmentBand.bandLine
                    : 'Live rules with no direct Met Diagnostics row — never folded into the ten sections above'}
                </span>
              </p>
              {model.equipmentBand ? (
                <Lock className="h-3.5 w-3.5 shrink-0 text-muted" aria-label="Fixed limit" />
              ) : null}
            </div>
            {model.ungrouped.length > 0 ? (
              <ul className={cn(STAGE_ROWS, 'mt-1')}>
                {model.ungrouped.map((row) => (
                  <li key={row.rule.id} className="py-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-muted">{row.rule.id}</span>
                      <Pill tone="default">{row.rule.priority}</Pill>
                      <ProvenanceChip
                        text={PROVENANCE_LABEL[row.provenanceKind]}
                        title={row.provenanceText}
                        compact
                      />
                    </div>
                    <p className="mt-1 font-medium text-ink-primary">
                      {row.rule.diagnosis}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2.5">
          {model.footerProposedCount > 0 ? (
            <p className="text-xs text-muted">
              {model.footerProposedCount} proposed {model.footerProposedCount === 1 ? 'change' : 'changes'}{' '}
              awaiting approval across the full review queue
            </p>
          ) : <span />}
          <button type="button" className={STAGE_INK_LINK} title="Mock affordance — no file leaves the browser">
            Download {model.downloadLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
